import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Page, PageHeader, Stats, StatusTabs, Controls, Toolbar, Search, List, Row, Empty, TextLink, Loading, FieldLabel , Meta } from "@/components/calm/Calm";

const STAGE_LABELS = { sourced: 'Sourced', contacted: 'Contacted', interviewing: 'Interviewing', offer: 'Offer', joined: 'Joined', lost: 'Lost' };
const STAGES = Object.keys(STAGE_LABELS);

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

const EMPTY_CANDIDATE = { name: '', email: '', phone: '', current_brokerage: '', notes: '' };

export default function RecruitPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [activeTab, setActiveTab] = useState('All');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_CANDIDATE);
  const [creating, setCreating] = useState(false);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/recruit/candidates`, { headers }).then(r => r.json()).then(d => { setCandidates(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleAdd = async () => {
    if (!form.name) return toast.error("Name is required");
    setCreating(true);
    try {
      const res = await fetch(`${BASE}/api/recruit/candidates`, { method: 'POST', headers, body: JSON.stringify(form) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Failed to add candidate');
      toast.success("Candidate added");
      setShowAdd(false);
      setForm(EMPTY_CANDIDATE);
      load();
    } catch (e) { toast.error(e.message || "Failed to add candidate"); }
    setCreating(false);
  };

  const handleStageChange = async (id, stage) => {
    setCandidates(cs => cs.map(c => c.id === id ? { ...c, stage } : c));
    try {
      const res = await fetch(`${BASE}/api/recruit/candidates/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ stage }) });
      if (!res.ok) throw new Error();
    } catch { toast.error("Failed to update stage"); load(); }
  };

  const handlePushToProspectors = async (id) => {
    try {
      const res = await fetch(`${BASE}/api/recruit/candidates/${id}/push-to-prospectors`, { method: 'POST', headers });
      if (!res.ok) throw new Error();
      toast.success("Added to your lead pipeline");
    } catch { toast.error("Failed to push candidate"); }
  };

  const TABS = ['All', ...STAGES.map(s => STAGE_LABELS[s])];
  const filtered = candidates.filter(c => {
    if (activeTab !== 'All' && c.stage !== activeTab.toLowerCase()) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.current_brokerage?.toLowerCase().includes(q);
  });

  if (loading) return <Loading />;

  const link = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: p.ink2, fontFamily: F.display, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS };
  const inPlay = candidates.filter(c => ['contacted', 'interviewing', 'offer'].includes(c.stage)).length;

  return (
    <Page testid="recruit-page" width={960}>
      <PageHeader title="Recruit" subtitle="Agents you're talking to about joining your office."
        action={<Btn variant="primary" dark={dark} onClick={() => setShowAdd(true)}>Add candidate</Btn>} />

      <Stats items={[
        { label: 'Candidates', value: candidates.length },
        { label: 'In conversation', value: inPlay },
        { label: 'Joined', value: candidates.filter(c => c.stage === 'joined').length },
      ]} />

      <Controls>
        <StatusTabs label="Stage" options={TABS.map(t => ({ key: t, label: t }))} value={activeTab} onChange={setActiveTab} testid="recruit-tabs" />
        <Toolbar><Search value={search} onChange={setSearch} placeholder="Search candidates" testid="recruit-search" /></Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty action={search ? null : <TextLink onClick={() => setShowAdd(true)}>Add your first candidate</TextLink>}>
          {search ? 'No candidates match your search.' : activeTab === 'All' ? 'No candidates yet.' : `No one at ${activeTab.toLowerCase()} right now.`}
        </Empty>
      ) : (
        <List testid="recruit-candidates">
          {filtered.map(c => (
            <Row key={c.id} testid={`candidate-${c.id}`}
              title={c.name}
              meta={[c.current_brokerage, c.created_at ? `Added ${fmtDate(c.created_at)}` : null]}
              status={STAGE_LABELS[c.stage] || c.stage}
              next={<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Meta parts={[c.email, c.phone ? { text: c.phone, figure: true } : null]} />
                {c.notes ? <Meta parts={[c.notes]} /> : null}
                <span style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select aria-label={`Change stage for ${c.name}`} value="" onChange={e => { if (e.target.value) handleStageChange(c.id, e.target.value); }}
                    style={{ border: 'none', background: 'none', outline: 'none', cursor: 'pointer', fontFamily: F.display, fontSize: 13, color: p.ink2, padding: 0, textDecoration: 'underline' }}>
                    <option value="" disabled>Change stage</option>
                    {STAGES.filter(st => st !== c.stage).map(st => <option key={st} value={st}>Move to {STAGE_LABELS[st].toLowerCase()}</option>)}
                  </select>
                  <button style={link} onClick={() => handlePushToProspectors(c.id)}>Add as lead</button>
                </span>
              </div>} />
          ))}
        </List>
      )}

      {/* Add Candidate Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Add candidate</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Track an agent you're recruiting to join your office.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><FieldLabel>Name</FieldLabel>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><FieldLabel>Email</FieldLabel>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div><FieldLabel>Phone</FieldLabel>
                <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (416) 555-0100"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div><FieldLabel>Current office</FieldLabel>
              <input type="text" value={form.current_brokerage} onChange={e => setForm({ ...form, current_brokerage: e.target.value })} placeholder="Current office"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div><FieldLabel>Notes</FieldLabel>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Where you found them, why they're a fit..."
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleAdd} disabled={creating}>{creating ? 'Adding...' : 'Add candidate'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
